import {Component, ViewEncapsulation} from '@angular/core';
import {CommonConstants, CountryCodes, RoutesConstants, ImagePathConstants} from '@coreconstant';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {noWhitespaceValidator} from '../core/services/common-service/common.service';
import {lastValueFrom} from 'rxjs';
import {ResponseHandlerService} from '../core/services/common-service/response-handler.service';
import {RouterModule} from '@angular/router';
import {NgSelectModule} from '@ng-select/ng-select';
import {IMaskModule} from 'angular-imask';
import {SeoService} from '../core/services/common-service/seo.service';
import {RecaptchaModule} from 'ng-recaptcha-2';
import {FaqCtaComponent} from '../core/component/common/faq-cta/faq-cta.component';
import {WeddingPlanCtaComponent} from '../core/component/common/wedding-plan-cta/wedding-plan-cta.component';
import {MetaDataConstants} from '../core/constants/meta-data-constants';
import {ContactEnquiryService} from '../core/services/api-service/contact-enquiry.service';
import {EnquiryReasonService} from '../core/services/api-service/enquiry-reason.service';
import {RecaptchaService} from '../core/services/api-service/recaptcha.service';

@Component({
    selector: 'app-get-in-touch',
    imports: [RouterModule, FormsModule, ReactiveFormsModule, NgSelectModule, IMaskModule, RecaptchaModule, FaqCtaComponent, WeddingPlanCtaComponent],
    templateUrl: './get-in-touch.component.html',
    styleUrls: ['../how-it-works/contact-us/contact-us.component.scss', './get-in-touch.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class GetInTouchComponent {
    routesConstants = RoutesConstants
    imagePathConstants = ImagePathConstants
    commonConstants = CommonConstants
    getInTouchDetailForm: FormGroup;
    enquiryReasons: any;
    countryCodes = CountryCodes;
    submitted: boolean = false;
    isSubmitAndShowSuccess: boolean = false

    constructor(
        private _formBuilder: FormBuilder,
        private _seoService: SeoService,
        private _recaptchaService: RecaptchaService,
        private _contactEnquiryService: ContactEnquiryService,
        private _enquiryReasonService: EnquiryReasonService,
        private _responseHandlerService: ResponseHandlerService
    ) {
        this.setMetaTags()
    }

    /**
     * Initializes the component and its dependent processes.
     */
    ngOnInit() {
        this.getEnquiryReasons();
        this.initializeForm();
    }

    /**
     * Sets the meta tags for the current page by creating a meta tag object and passes it to the SEO service for application.
     * @return {void} Does not return a value.
     */
    setMetaTags() {
        let metaTagObject = {
            title: MetaDataConstants.GET_IN_TOUCH.TITLE,
            description: MetaDataConstants.GET_IN_TOUCH.DESCRIPTION,
            ogImageUrl: MetaDataConstants.GET_IN_TOUCH.OGIMAGEURL,
            ogWebUrl: this.routesConstants.GET_IN_TOUCH,
        }
        this._seoService.setMetaTags(metaTagObject)
    }

    /**
     * Initializes the form and applies necessary validation rules to each control.
     */
    initializeForm() {
        this.getInTouchDetailForm = this._formBuilder.group({
            contactName: ["", [Validators.required, noWhitespaceValidator]],
            countryCode: [this.commonConstants.AUSTRALIA_COUNTRY_CODE, [Validators.required]],
            contactNumber: [null, [Validators.required, Validators.pattern(this.commonConstants.MOBILE_REGEX)]],
            contactEmail: ["", [Validators.required, Validators.pattern(this.commonConstants.EMAIL_REGEX)]],
            enquiryReasonId: [null, Validators.required],
            contactMessage: ["", Validators.required],
            recaptcha: ['', Validators.required],
        });
    }

    /**
     * Handles the resolution of a CAPTCHA, verifying the user's response and updating the form based on the verification result.
     * @param {string} captchaResponse - The CAPTCHA response token generated after user completes the CAPTCHA challenge.
     */
    onCaptchaResolved(captchaResponse: string) {
        let input = {
            token: captchaResponse,
        }
        let verifyCaptchaResponse = this._recaptchaService.verifyCaptcha(input)
        lastValueFrom(verifyCaptchaResponse).then((response: any) => {
            if (response["status"] == true) {
                if (response["data"]["success"] == true) {
                    this.getInTouchDetailForm.patchValue({recaptcha: response["data"]});
                } else {
                    this.getInTouchDetailForm.patchValue({recaptcha: null});
                }

            }
        }, (error: any) => {
            this._responseHandlerService.handleAPIErrorResponse(error);
        });
    }

    /**
     * Fetches the enquiry reasons from the service and updates the local data.
     */
    getEnquiryReasons() {
        let getListing = this._enquiryReasonService.getEnquiryReasons()
        lastValueFrom(getListing).then((res: any) => {
            if (res["status"]) {
                this.enquiryReasons = res["data"]
            }
        }, (error: any) => {
            this._responseHandlerService.handleAPIErrorResponse(error)
        });
    }

    /**
     * Submits the form after validating form, prepares the necessary form data, and initiates the API call.
     */
    submit() {
        this.submitted = true;
        if (this.getInTouchDetailForm.invalid) {
            this.getInTouchDetailForm.markAllAsTouched();
            this.getInTouchDetailForm.markAsDirty();
            return
        }
        const flattenedValues = {
            ...this.getInTouchDetailForm.value,
        }
        let addUpdateRecord = this._contactEnquiryService.submitContactEnquiry(flattenedValues)
        lastValueFrom(addUpdateRecord).then((response: any) => {
            if (response["status"] == true) {
                this.isSubmitAndShowSuccess = true
            }
        }, (error: any) => {
            this._responseHandlerService.handleAPIErrorResponse(error);
        });
    }
}
